/**
 * Detects a failed WebContainer `build` tool in assistant UI messages.
 * Used when the stream ends with `finishReason: "stop"` after a tool round.
 */

import { isBuildToolResultFailure } from './prototyperBuildPhases';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function isBuildToolPart(part: Record<string, unknown>): boolean {
  const t = part.type;
  if (t === 'tool-build') return true;
  if (t === 'dynamic-tool' && part.toolName === 'build') return true;
  return false;
}

function extractBuildOutputFromPart(part: Record<string, unknown>): string | undefined {
  if (typeof part.output === 'string') return part.output;
  if (typeof part.result === 'string') return part.result;
  if (part.state === 'output-error' && typeof part.errorText === 'string') {
    return part.errorText;
  }
  return undefined;
}

/** Last completed build step in toolSteps (facade-owned, always up to date). */
export function lastToolStepBuildFailed(
  toolSteps: readonly { toolName: string; isComplete: boolean; result?: string }[],
): boolean {
  const lastBuild = [...toolSteps].reverse().find((s) => s.toolName === 'build' && s.isComplete);
  if (!lastBuild?.result) return false;
  return isBuildToolResultFailure(lastBuild.result);
}

/**
 * @returns true if the most recent build tool output (scanning assistant messages
 *          newest-first) indicates failure.
 */
export function lastAssistantClientBuildFailedInUiMessages(
  messages: readonly unknown[] | undefined,
): boolean {
  if (!Array.isArray(messages) || messages.length === 0) return false;

  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!isRecord(m) || m.role !== 'assistant') continue;

    const parts = m.parts;
    if (!Array.isArray(parts)) continue;

    let lastBuildOutput: string | undefined;
    for (const part of parts) {
      if (!isRecord(part) || !isBuildToolPart(part)) continue;
      const out = extractBuildOutputFromPart(part);
      if (out !== undefined) lastBuildOutput = out;
    }

    if (lastBuildOutput !== undefined) {
      return isBuildToolResultFailure(lastBuildOutput);
    }
  }

  return false;
}

/** UI messages and/or local toolSteps — either can signal a failed build. */
export function shouldAutoContinueAfterFailedBuild(
  messages: readonly unknown[] | undefined,
  toolSteps: readonly { toolName: string; isComplete: boolean; result?: string }[],
): boolean {
  return (
    lastAssistantClientBuildFailedInUiMessages(messages) ||
    lastToolStepBuildFailed(toolSteps)
  );
}
